import './MapModeBar.css';
import { useEffect, useId, useRef } from 'preact/hooks';
import { Icon } from '../../components/Icon';
import { Portrait } from '../../components/Portrait';
import { type CopyKey, t } from '../../copy/strings';
import type { Candidate, CandidateNumber, MapMode, ResultsView, RouteState } from '../../model/types';
import { setMapCandidate, setMapMode, togglePicker } from '../../state/actions';
import {
  useConfig,
  useFocusUf,
  useMapMode,
  useRouteState,
  useStatics,
  useTimeline,
  useUi,
  useView,
} from '../../state/hooks';
import { CandidatePicker } from './CandidatePicker';

/** Botões possíveis da barra (§9.3): "Cor" substitui Municípios e Estados com UF em foco. */
export type ModeButton = 'municipalities' | 'states' | 'color' | 'advantage' | 'counted' | 'candidate';

const LABEL: Record<ModeButton, CopyKey> = {
  municipalities: 'map.mode.municipalities',
  states: 'map.mode.states',
  color: 'map.mode.color',
  advantage: 'map.mode.advantage',
  counted: 'map.mode.counted',
  candidate: 'map.mode.candidate',
};

/**
 * Botões por contexto (§9.3, tabela "Contexto"): Brasil = Municípios · Estados · Vantagem¹ · Apurado · Candidato;
 * UF em foco = Cor · Vantagem¹ · Apurado · Candidato; sem `municipal_map`, nem Municípios nem Vantagem (¹ só com
 * `advantage_mode`, que precisa da malha municipal).
 */
export function modeButtons(focus: boolean, f: { advantage_mode: boolean; municipal_map: boolean }): ModeButton[] {
  const list: ModeButton[] = focus ? ['color'] : f.municipal_map ? ['municipalities', 'states'] : ['states'];
  if (f.advantage_mode && f.municipal_map) list.push('advantage');
  list.push('counted', 'candidate');
  return list;
}

/** `aria-pressed` pelo modo EFETIVO; "Cor" fica pressionado em Municípios e em Estados (§9.3). */
export function isPressed(button: ModeButton, effective: MapMode): boolean {
  if (button === 'color') return effective === 'municipalities' || effective === 'states';
  return button === effective;
}

/** Par de candidatos a presidente: da vista (ao vivo ou replay) ou do cadastro antes do 1º snapshot. */
export function presidentPair(view: ResultsView | null, list: readonly Candidate[] | undefined | null) {
  const pair = view?.candidates ?? list ?? null;
  return pair && pair.length === 2 ? (pair as readonly [Candidate, Candidate]) : null;
}

/** Lado do candidato do modo (§9.4.7): o da URL; inválido → líder nacional → {A}. */
export function candidateSide(
  pair: readonly [Candidate, Candidate],
  wanted: CandidateNumber | null,
  leader: 0 | 1 | null,
): 0 | 1 {
  if (pair[0].number === wanted) return 0;
  if (pair[1].number === wanted) return 1;
  return leader ?? 0;
}

/** Número ao entrar no modo Candidato (§9.3): o da URL, ou o do líder nacional, ou o de {A}. */
export function enterCandidate(
  pair: readonly [Candidate, Candidate],
  wanted: CandidateNumber | null,
  leader: 0 | 1 | null,
): CandidateNumber {
  return wanted ?? pair[leader ?? 0].number;
}

export interface MapModeBarProps {
  /** Altura máxima do seletor (altura do retângulo do mapa − 12, §9.3); sem ela, o CSS limita. */
  pickerMaxHeight?: number;
}

/**
 * "O mapa mostra" (§9.3): grupo de botões com `aria-pressed` pelo modo efetivo. Clique num modo → `setMapMode` (uma
 * entrada no histórico, §5.3.1); no modo já ativo, nada. Candidato fora do modo grava `mapa=candidato&cand=…` numa só
 * navegação e abre o `CandidatePicker`; no modo, abre ou fecha. No replay o Candidato fica `aria-disabled` (continua
 * focável) com o motivo em `aria-describedby` (§9.4.11). Quem decide se a barra existe é a `MapTopBar`.
 */
export function MapModeBar({ pickerMaxHeight }: MapModeBarProps) {
  const route = useRouteState();
  const effective = useMapMode();
  const { live } = useTimeline();
  const ui = useUi();
  const view = useView();
  const statics = useStatics();
  const config = useConfig();
  const focus = useFocusUf() !== null;
  const reasonId = useId();
  const pickerId = useId();
  const candidateRef = useRef<HTMLButtonElement>(null);

  const pair = presidentPair(view, statics.candidates?.president);
  const leader = view?.national.leader ?? null;
  const inCandidate = effective === 'candidate';
  const open = ui.pickerOpen && inCandidate && live && pair !== null;
  const side = pair ? candidateSide(pair, route.mapCandidate, leader) : 0;
  const chosen = pair ? pair[side] : null;

  // O foco volta ao botão quando o seletor fecha com o foco dentro dele (Esc, escolha; §5.9, §15.2.5).
  const wasOpen = useRef(open);
  useEffect(() => {
    if (wasOpen.current && !open) {
      const active = document.activeElement;
      if (!active || active === document.body) candidateRef.current?.focus({ preventScroll: true });
    }
    wasOpen.current = open;
  }, [open]);
  // Fora do modo, do ao vivo ou sem candidatos, o seletor não pode ficar marcado como aberto.
  useEffect(() => {
    if (ui.pickerOpen && !open) togglePicker(false);
  }, [ui.pickerOpen, open]);
  // Candidato fora do modo: o seletor abre quando a rota chega ao modo Candidato. A rota muda no `hashchange`, depois
  // do clique; abrir no clique faria o efeito acima fechar o seletor antes de a rota chegar. Outra navegação no meio
  // cancela a abertura.
  const openOnEnter = useRef<RouteState | null>(null);
  useEffect(() => {
    const from = openOnEnter.current;
    if (!from) return;
    if (inCandidate) {
      openOnEnter.current = null;
      if (live && pair) togglePicker(true);
    } else if (route !== from) {
      openOnEnter.current = null;
    }
  }, [route, inCandidate]);

  const onMode = (b: ModeButton) => {
    if (b === 'candidate') {
      if (!live || !pair) return;
      if (inCandidate) {
        togglePicker();
        return;
      }
      openOnEnter.current = route;
      setMapCandidate(enterCandidate(pair, route.mapCandidate, leader));
      return;
    }
    if (isPressed(b, effective)) return;
    setMapMode(b === 'color' ? 'municipalities' : b);
  };

  const buttons = modeButtons(focus, config.features);
  return (
    // biome-ignore lint/a11y/useSemanticElements: §9.3 e §15.4.2 pedem <div role="group">; <fieldset> não aceita inline-flex de forma confiável e exige <legend>
    <div class="map-mode-bar" role="group" aria-label={t('map.modes.aria')} data-testid="map-mode-bar">
      {buttons.map((b) => {
        if (b !== 'candidate') {
          return (
            <button
              key={b}
              type="button"
              class="map-mode-bar__button"
              data-mode={b}
              aria-pressed={isPressed(b, effective)}
              onClick={() => onMode(b)}
            >
              {t(LABEL[b])}
            </button>
          );
        }
        const named = inCandidate && chosen !== null;
        return (
          <span key={b} class="map-mode-bar__candidate-wrap">
            <button
              ref={candidateRef}
              type="button"
              class={`map-mode-bar__button map-mode-bar__candidate${named ? ' map-mode-bar__candidate--named' : ''}`}
              data-mode={b}
              aria-pressed={inCandidate}
              aria-haspopup="listbox"
              aria-expanded={open}
              aria-controls={open ? pickerId : undefined}
              aria-disabled={live ? undefined : 'true'}
              aria-describedby={live ? undefined : reasonId}
              aria-label={named && chosen ? t('map.picker.button.aria', { NOME: chosen.display_name }) : undefined}
              title={live ? t('map.candidate.title') : t('map.candidate.disabled')}
              onClick={() => onMode(b)}
            >
              {named && chosen ? (
                <>
                  <span class="map-mode-bar__portrait">
                    <Portrait
                      candidate={chosen}
                      photo={config.features.photos ? chosen.photo : null}
                      size={18}
                      shape="round"
                    />
                  </span>
                  <span class="map-mode-bar__name">{chosen.display_name}</span>
                </>
              ) : (
                <span>{t('map.mode.candidate')}</span>
              )}
              <Icon name="caret-down" size={10} />
            </button>
            {live ? null : (
              <span id={reasonId} class="sr-only">
                {t('map.candidate.disabled')}
              </span>
            )}
            {open && pair ? (
              <CandidatePicker
                id={pickerId}
                pair={pair}
                selected={side}
                shares={view && view.national.validVotes > 0 ? view.national.shares : null}
                photos={config.features.photos}
                maxHeight={pickerMaxHeight}
                anchor={candidateRef}
              />
            ) : null}
          </span>
        );
      })}
    </div>
  );
}
